/* global globalThis */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import styles from "./stickyScrollShell.module.css";

const measureStickyEdge = (shell) => {
    const table = shell.firstElementChild;
    if (!table) return null;
    const headerRow = table.querySelector("thead tr") || table.querySelector("tr");
    if (!headerRow) return null;
    const shellRect = shell.getBoundingClientRect();
    let edge = 0;
    Array.from(headerRow.children).forEach((cell) => {
        const computed = globalThis.getComputedStyle(cell);
        if (computed.position === "sticky" && computed.left !== "auto") {
            edge = Math.max(edge, cell.getBoundingClientRect().right - shellRect.left);
        }
    });
    return edge > 0 ? edge : null;
};

export default function StickyScrollShell({ frozenWidth = 0, children }) {
    const shellRef = useRef(null);
    const proxyRef = useRef(null);
    const syncing = useRef(false);
    const [metrics, setMetrics] = useState({ content: 0, client: 0, frozenWidth });

    const measure = useCallback(() => {
        const shell = shellRef.current;
        if (!shell) return;
        const measuredFrozenWidth = measureStickyEdge(shell) ?? frozenWidth;
        setMetrics((prev) =>
            prev.content === shell.scrollWidth && prev.client === shell.clientWidth && prev.frozenWidth === measuredFrozenWidth
                ? prev
                : { content: shell.scrollWidth, client: shell.clientWidth, frozenWidth: measuredFrozenWidth }
        );
    }, [frozenWidth]);

    useLayoutEffect(() => {
        measure();
        const shell = shellRef.current;
        if (!shell || typeof ResizeObserver === "undefined") return undefined;
        const ro = new ResizeObserver(measure);
        ro.observe(shell);
        const table = shell.firstElementChild;
        if (table) ro.observe(table);
        return () => ro.disconnect();
    }, [measure, children]);

    const syncFrom = (sourceRef, targetRef) => {
        if (syncing.current) {
            syncing.current = false;
            return;
        }
        const source = sourceRef.current;
        const target = targetRef.current;
        if (source && target && target.scrollLeft !== source.scrollLeft) {
            syncing.current = true;
            target.scrollLeft = source.scrollLeft;
        }
    };

    const onShellScroll = () => syncFrom(shellRef, proxyRef);
    const onProxyScroll = () => syncFrom(proxyRef, shellRef);

    useEffect(() => {
        const shell = shellRef.current;
        if (!shell) return undefined;
        const onWheel = (event) => {
            if (!event.deltaX) return;
            const proxy = proxyRef.current;
            if (!proxy) return;
            const max = proxy.scrollWidth - proxy.clientWidth;
            if (max <= 0) return;
            const next = Math.min(max, Math.max(0, proxy.scrollLeft + event.deltaX));
            if (next !== proxy.scrollLeft) {
                event.preventDefault();
                proxy.scrollLeft = next;
            }
        };
        shell.addEventListener("wheel", onWheel, { passive: false });
        return () => shell.removeEventListener("wheel", onWheel);
    }, []);

    const hasOverflow = metrics.content > metrics.client + 1;

    return (
        <Box className={styles.wrapper}>
            <Box className={styles.shell} ref={shellRef} onScroll={onShellScroll}>
                {children}
            </Box>
            {hasOverflow && (
                <Box
                    className={styles.proxy}
                    ref={proxyRef}
                    onScroll={onProxyScroll}
                    style={{ left: metrics.frozenWidth }}
                >
                    <Box
                        className={styles.proxyTrack}
                        style={{ width: Math.max(0, metrics.content - metrics.frozenWidth) }}
                    />
                </Box>
            )}
        </Box>
    );
}

StickyScrollShell.propTypes = {
    frozenWidth: PropTypes.number,
    children: PropTypes.node,
};